import { FRAME_SECONDS } from '../constants'
import { BehaviorContext, BehaviorHandler } from '../types'

/**
 * アバター死亡（体力 0）の毎フレーム監視（Lua: Humanoid.Died の代替）。
 * rjf10 / rjs07 / rjs08 で重複していた死亡監視ループを 1 箇所へ集約する。
 *
 * - once: true は最初の死亡検知で onDeath を 1 回呼んで監視を終える
 *   （Lua: Died を一度だけ処理する isGameOver ガード相当）。
 * - once: false は「生存 → 体力 0」のエッジのたびに onDeath を呼び続ける
 *   （実機がリスポーンごとに Died を張り直し、死亡のたびに発火するのと同じ）。
 */
export interface DeathWatcherOptions {
  /** true なら最初の死亡で監視を終える。false ならエッジごとに発火し続ける。 */
  readonly once: boolean
  /** 死亡検知時に呼ぶ処理（同期）。 */
  readonly onDeath: (ctx: BehaviorContext) => void
  /**
   * 監視を続ける外部条件。false を返すと死亡前でも監視を終える
   * （Lua: 別経路でゲームオーバーが立ったらループを抜ける while 条件の再現）。
   */
  readonly shouldContinue?: () => boolean
}

/** 死亡監視の常駐ハンドラを作る。onPlay に登録する。 */
export const createDeathWatcher = (
  options: DeathWatcherOptions,
): BehaviorHandler => {
  const { once, onDeath, shouldContinue } = options
  return async (ctx) => {
    // 直前フレームの死亡状態（「生存 → 死亡」のエッジ検知用）。
    let wasDead = false
    while (!ctx.signal.aborted && (shouldContinue?.() ?? true)) {
      const isDead = ctx.world.getAvatarHealth() <= 0
      if (isDead && !wasDead) {
        onDeath(ctx)
        if (once) {
          return
        }
      }
      wasDead = isDead
      await ctx.wait(FRAME_SECONDS)
    }
  }
}
